import { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';

function EditEntreprise() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    secteur: '',
    autreSecteur: '',
    localisation: '',
    siteWeb: '',
    logo: '',
    telephone: '',
    email: ''
  });

  const [previewImage, setPreviewImage] = useState(null);
  const { userRole } = useAuth();

  useEffect(() => {
    const fetchEntreprise = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des données');
        }

        const data = await response.json();
        
        // Liste des secteurs prédéfinis
        const secteursPredefinis = ["Informatique", "Conseil", "Finance", "Industrie", "Commerce"];
        
        // Vérifier si le secteur est dans la liste prédéfinie
        const secteurExiste = secteursPredefinis.includes(data.secteur);
        
        // Initialiser formData avec les données
        setFormData({
          nom: data.nom || '',
          description: data.description || '',
          secteur: secteurExiste ? data.secteur : 'Autre',
          autreSecteur: secteurExiste ? '' : data.secteur,
          localisation: data.localisation || '',
          siteWeb: data.siteWeb || '',
          logo: data.logo || '',
          telephone: data.telephone || '',
          email: data.email || ''
        });

        // Ne définir previewImage que si logo existe
        if (data.logo) {
          setPreviewImage(data.logo);
        }

      } catch (err) {
        console.error('Erreur détaillée:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchEntreprise();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'secteur' && value !== 'Autre' && { autreSecteur: '' })
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        setError("L'image ne doit pas dépasser 5MB");
        return;
      }

      setFormData(prev => ({
        ...prev,
        logo: file
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSubmit = {
        ...formData,
        secteur: formData.secteur === 'Autre' ? formData.autreSecteur : formData.secteur
      };
      delete dataToSubmit.autreSecteur;

      if (formData.logo && formData.logo instanceof File) {
        const formDataLogo = new FormData();
        formDataLogo.append('file', formData.logo);
        formDataLogo.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET_LOGO);

        const cloudinaryResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/upload`,
          {
            method: 'POST',
            body: formDataLogo
          }
        );

        if (!cloudinaryResponse.ok) {
          throw new Error("Erreur lors de l'upload du logo");
        }

        const cloudinaryData = await cloudinaryResponse.json();
        dataToSubmit.logo = cloudinaryData.secure_url;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(dataToSubmit)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la modification');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/entreprises');
      }, 2000);
    } catch (err) {
      setError("Une erreur est survenue lors de la modification de l'entreprise");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Utilisation de la bonne route selon le rôle
      const endpoint = userRole === ROLES.ADMIN 
        ? `${process.env.REACT_APP_API_BASE_URL}/admin/entreprises/${id}`
        : `${process.env.REACT_APP_API_BASE_URL}/pilote/entreprises/${id}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erreur lors de la suppression de l'entreprise");
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/entreprises');
      }, 2000);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      setError("Une erreur est survenue lors de la suppression de l'entreprise");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/entreprises')}
            variant="contained"
            color="primary"
          >
            Retour aux entreprises
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Modifier l'entreprise
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Entreprise modifiée avec succès! Redirection...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            label="Nom de l'entreprise"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            multiline
            rows={4}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Secteur d'activité</InputLabel>
            <Select
              required
              name="secteur"
              value={formData.secteur}
              label="Secteur d'activité"
              onChange={handleChange}
            >
              <MenuItem value="Informatique">Informatique</MenuItem>
              <MenuItem value="Conseil">Conseil</MenuItem>
              <MenuItem value="Finance">Finance</MenuItem>
              <MenuItem value="Industrie">Industrie</MenuItem>
              <MenuItem value="Commerce">Commerce</MenuItem>
              <MenuItem value="Autre">Autre</MenuItem>
            </Select>
          </FormControl>

          {formData.secteur === 'Autre' && (
            <TextField
              required
              fullWidth
              label="Précisez le secteur"
              name="autreSecteur"
              value={formData.autreSecteur}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            required
            fullWidth
            label="Localisation"
            name="localisation"
            value={formData.localisation}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Site Web"
            name="siteWeb"
            value={formData.siteWeb}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Email de contact"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Téléphone"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="logo-upload"
              type="file"
              onChange={handleImageChange}
            />
            <label htmlFor="logo-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 1 }}
              >
                {previewImage ? 'Changer le logo' : 'Ajouter un logo'}
              </Button>
            </label>

            {previewImage && (
              <Box sx={{ 
                position: 'relative', 
                width: 'fit-content', 
                margin: '0 auto'
              }}>
                <img
                  src={previewImage}
                  alt="Aperçu du logo"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '10px auto'
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    bgcolor: 'background.paper'
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDeleteDialog(true)}
              disabled={loading}
            >
              Supprimer l'entreprise
            </Button>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/entreprises')}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                color="primary"
              >
                {loading ? 'Modification...' : 'Modifier l\'entreprise'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Dialog de confirmation de suppression */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>
            Confirmer la suppression
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleDelete}
              color="error"
              disabled={loading}
              variant="contained"
            >
              {loading ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>

      </Paper>
    </Container>
  );
}

export default EditEntreprise; 